// Shared presentational components (§11 house style). Dense, information-first, no emoji.

import type { ReactNode } from "react";
import type { RunStatus } from "../lib/api";

export function Card({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="card">
      {(title || actions) && (
        <div className="mb-3 flex items-center justify-between">
          {title && <h2 className="text-sm font-semibold text-slate-200">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function Tile({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "ok" | "warn" | "danger" }) {
  const toneClass =
    tone === "danger" ? "text-danger" : tone === "warn" ? "text-accent" : tone === "ok" ? "text-ok" : "text-slate-100";
  return (
    <div className="card">
      <div className="label">{label}</div>
      <div className={`font-mono text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub != null && <div className="mt-1 text-xs text-muted">{sub}</div>}
    </div>
  );
}

const STATUS_TONE: Record<RunStatus | string, string> = {
  succeeded: "bg-ok/15 text-ok border-ok/40",
  running: "bg-primary/15 text-primary border-primary/40",
  pending: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  deferred: "bg-accent/15 text-accent border-accent/40",
  failed: "bg-danger/15 text-danger border-danger/40",
  dead: "bg-danger/25 text-danger border-danger/60",
  skipped: "bg-slate-600/15 text-muted border-slate-600/40",
};

export function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_TONE[status] ?? "bg-slate-600/15 text-muted border-slate-600/40";
  return (
    <span className={`inline-block rounded border px-1.5 py-0.5 font-mono text-[11px] uppercase ${cls}`}>
      {status}
    </span>
  );
}

export function Badge({ children, tone }: { children: ReactNode; tone?: "ok" | "warn" | "danger" | "muted" }) {
  const cls =
    tone === "ok"
      ? "bg-ok/15 text-ok border-ok/40"
      : tone === "warn"
        ? "bg-accent/15 text-accent border-accent/40"
        : tone === "danger"
          ? "bg-danger/15 text-danger border-danger/40"
          : "bg-slate-600/15 text-muted border-slate-600/40";
  return <span className={`inline-block rounded border px-1.5 py-0.5 font-mono text-[11px] ${cls}`}>{children}</span>;
}

export function Spinner() {
  return <div className="py-8 text-center text-sm text-muted">Loading…</div>;
}

export function ErrorBox({ error }: { error: Error }) {
  return (
    <div className="rounded border border-danger/50 bg-danger/10 p-3 text-sm text-danger">
      {error.message}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="py-8 text-center text-sm text-muted">{children}</div>;
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-3">
      <label className="label">{label}</label>
      {children}
      {hint != null && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}